import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useLocalizedWeekDays } from "../../hooks/useDateLocalization";
import { trackEvent } from "../../utils/analytics";
import { type RecurringShift, ShiftType } from "../../utils/models";

interface PreferencesCalendarProps {
	value?: DateTime;
	onChange?: (date: DateTime) => void;
	startDate?: DateTime;
	isOpen?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
	preferences?: Array<{
		weekIndex: number;
		dayIndex: number;
		preferDay: boolean;
		preferNight: boolean;
	}>;
}

export function PreferencesCalendar({ value = DateTime.now(), onChange, startDate, isOpen, onOpenChange, preferences = [] }: PreferencesCalendarProps) {
	const { hospitalData, locationsMap } = useHospitalData();
	const { userInfo } = useAuth();
	const { t, i18n } = useTranslation();
	const [internalStartDate, setInternalStartDate] = useState<DateTime>(() => {
		if (startDate) {
			return startDate;
		}
		return value.startOf("week");
	});
	const weekDays = useLocalizedWeekDays();

	// Update internalStartDate when language changes to ensure week alignment
	useEffect(() => {
		if (!startDate) {
			setInternalStartDate((prev) => prev.startOf("week"));
		}
	}, [i18n.language, startDate]);

	// Track component mount
	useEffect(() => {
		trackEvent("preferences_calendar_mounted", {
			user_id: userInfo?.id,
			start_date: internalStartDate.toFormat("yyyy-MM-dd"),
			is_open: isOpen,
		});
	}, []);

	useEffect(() => {
		if (startDate) {
			setInternalStartDate(startDate);

			trackEvent("preferences_calendar_start_date_changed", {
				user_id: userInfo?.id,
				start_date: startDate.toFormat("yyyy-MM-dd"),
			});
		} else if (hospitalData?.weeks?.[0]?.weeklyShifts?.[0]?.days?.[0]?.date) {
			const firstAvailableDate = hospitalData.weeks[0].weeklyShifts[0].days[0].date;
			setInternalStartDate(firstAvailableDate.startOf("week"));

			trackEvent("preferences_calendar_start_date_auto_set", {
				user_id: userInfo?.id,
				start_date: firstAvailableDate.startOf("week").toFormat("yyyy-MM-dd"),
			});
		}
	}, [hospitalData, startDate, userInfo?.id]);

	const getShiftIndicator = (dateString: string): undefined | RecurringShift => {
		const nurse = hospitalData?.nurses.find((n: any) => n.userId === userInfo?.id);
		if (!nurse) return undefined;

		const formattedInputDate = DateTime.fromISO(dateString);
		const weekday = formattedInputDate.toFormat("cccc");
		const weekNumber = Math.floor(formattedInputDate.diff(internalStartDate, "day").days / 7);

		// TODO(DeleteMSFixes): Get the relevant recurring shift
		return undefined;
	};

	const getPreference = (date: DateTime) => {
		const weekIndex = Math.floor(date.diff(internalStartDate, "day").days / 7);
		// Calculate dayIndex relative to the week start (0-6, where 0 is the first day of the week in the current locale)
		const dayIndex = ((date.weekday % 7) & (7 - internalStartDate.weekday) & (7 + 7)) % 7;
		return preferences.find((p) => p.weekIndex === weekIndex && p.dayIndex === dayIndex);
	};

	const handleDateClick = (date: DateTime) => {
		trackEvent("preferences_calendar_date_clicked", {
			user_id: userInfo?.id,
			selected_date: date.toFormat("yyyy-MM-dd"),
			previous_date: value.toFormat("yyyy-MM-dd"),
			day_of_week: date.toFormat("cccc"),
			week_number: Math.floor(date.diff(internalStartDate, "day").days / 7) + 1,
		});

		if (onChange) {
			onChange(date);
		}
	};

	const renderCalendar = () => {
		const weeks = [];

		// Header row with weekdays
		weeks.push(
			<Box
				key="weekdays"
				sx={{
					display: "grid",
					gridTemplateColumns: "65px repeat(7, 1fr)",
					mb: 0.5,
				}}
			>
				<Typography
					variant="body2"
					sx={{
						textAlign: "center",
						color: (theme) => alpha(theme.palette.primary.dark, 0.5),
						py: 0,
						fontSize: "0.75rem",
						fontWeight: 600,
						letterSpacing: "0.02em",
						opacity: 0.8,
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-start",
					}}
				>
					{t("week")}
				</Typography>
				{weekDays.map((day) => (
					<Typography
						key={day}
						variant="body2"
						sx={{
							textAlign: "center",
							color: (theme) => alpha(theme.palette.primary.dark, 0.5),
							py: 0,
							fontSize: "0.75rem",
							fontWeight: 600,
							letterSpacing: "0.02em",
							opacity: 0.8,
						}}
					>
						{day}
					</Typography>
				))}
			</Box>,
		);

		// Generate dates for two weeks
		const dates = [];
		for (let i = 0; i < 14; i++) {
			const date = internalStartDate.plus({ day: i });
			dates.push(date);
		}

		// Split into weeks and render
		for (let i = 0; i < dates.length; i += 7) {
			const week = dates.slice(i, i + 7);
			weeks.push(
				<Box key={i} sx={{ display: "grid", gridTemplateColumns: "65px repeat(7, 1fr)" }}>
					<Typography
						variant="body2"
						sx={{
							textAlign: "center",
							color: (theme) => alpha(theme.palette.primary.dark, 0.5),
							py: 1,

							fontSize: "0.75rem",
							fontWeight: 600,
							letterSpacing: "0.02em",
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-start",
							position: "relative",
							top: -10,
						}}
					>
						{t("week")} {i === 0 ? "1" : "2"}
					</Typography>
					{week.map((date) => {
						const isSelected = value.startOf("day") === date.startOf("day");
						const dateString = date.toFormat("yyyy-MM-dd");
						const isToday = date.startOf("day") === DateTime.now().startOf("day");
						const shiftInfo = getShiftIndicator(dateString);
						// TODO(DeleteMSFixes): Get the correct shift type using the recurring shift hour
						const locInfo = shiftInfo === undefined ? undefined : { shift_type: ShiftType.Day };

						return (
							<Box
								key={date.toString()}
								onClick={() => handleDateClick(date)}
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									cursor: "pointer",
									position: "relative",
									touchAction: "none",
									mb: 1,
								}}
							>
								<Box
									sx={{
										width: "40px",
										height: "40px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										position: "relative",
										flexDirection: "column",
										fontWeight: 600,
										borderRadius: "50%",
										color: "primary.dark",
										...(isToday && {
											border: "2px solid #9e9e9e",
										}),
										...(isSelected && {
											bgcolor: "rgba(166,153,218,0.2)",
											color: "primary.dark",
										}),
										...(locInfo?.shift_type === ShiftType.Night && {
											bgcolor: "primary.main",
											color: "white",
										}),
										...(locInfo?.shift_type === ShiftType.Day && {
											bgcolor: "#FBBB00",
											color: "white",
										}),
										...(locInfo?.shift_type === ShiftType.All && {
											background: "linear-gradient(135deg, #FBBB00 50%, #5F5CE8 50%)",
											color: "white",
										}),
									}}
								/>
								{!locInfo?.shift_type && getPreference(date) && (
									<Box
										sx={{
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											gap: "2px",
										}}
									>
										{getPreference(date)?.preferDay && (
											<ThumbUpAltIcon
												sx={{
													fontSize: "0.875rem",
													color: "#FBBB00",
												}}
											/>
										)}
										{getPreference(date)?.preferNight && (
											<ThumbUpAltIcon
												sx={{
													fontSize: "0.875rem",
													color: "primary.main",
												}}
											/>
										)}
									</Box>
								)}
							</Box>
						);
					})}
				</Box>,
			);
		}

		return weeks;
	};

	return <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", p: 2, bgcolor: "white" }}>{renderCalendar()}</Box>;
}
